import { isBefore, isFuture, isValid, parse } from "date-fns";

export const scrollToElement = (
  elementRef: React.RefObject<HTMLDivElement>
) => {
  if (elementRef.current) {
    elementRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

export const formatString = (str: string): string => {
  return str
    ?.replace(/[-_]/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2") // Add a space between camelCase words
    .toLowerCase();
};

export function isValidDate(dateString: string): boolean {
  // Try to create a Date object from the string
  const date = new Date(dateString);

  // Check if the date is valid
  return !isNaN(date.getTime());
}

export const formatDate = (dateArg: Date) => {
  const date = new Date(dateArg);

  const year = date?.getFullYear();
  const month = String(date?.getMonth() + 1).padStart(2, "0");
  const day = String(date?.getDate()).padStart(2, "0");
  const formattedDate = `${day}/${month}/${year}`;
  return formattedDate;
};

export function truncateText(text: string, maxLength: number) {
  if (text.length <= maxLength) {
    return text;
  }
  return text.slice(0, maxLength) + "...";
}

export function maskString(value: string): string {
  // Ensure the value is long enough to mask
  if (value.length <= 2) {
    return value; // Return the string as is if it's too short to mask
  }

  // Mask all characters except the last two
  const masked = "*".repeat(value.length - 2) + value.slice(-2);

  return masked;
}
export function parseDate(dateString: string) {
  // Split the date string into day, month, and year
  const [day, month, year] = dateString.split("/");

  // Ensure all parts are valid
  if (!day || !month || !year) {
    throw new Error("Invalid date format. Expected format is DD/MM/YYYY.");
  }

  // Create and return the Date object
  return new Date(`${year}-${month}-${day}`);
}

export function parseDateToString(dateString: string) {
  // Split the date string into day, month, and year
  const [year, month, day] = dateString.split("/");

  // Ensure all parts are valid
  if (!day || !month || !year) {
    throw new Error("Invalid date format. Expected format is DD/MM/YYYY.");
  }

  // Create and return the Date object
  return `${year}/${day}/${month}`;
}

export function formatToValidDate(dateString: string) {
  // Split the date string into day, month, and year
  const [day, month, year] = dateString.split("/");

  // Ensure all parts are valid
  if (!day || !month || !year) {
    throw new Error("Invalid date format. Expected format is DD/MM/YYYY.");
  }

  // Create and return the Date object
  return `${year}-${month}-${day}`;
}

export function universalDateFormatter(dateString: string) {
  // Split the date string into day, month, and year
  if (!dateString) {
    return "";
  }
  const [year, month, day] = dateString.split("/");

  // Ensure all parts are valid
  if (!day || !month || !year) {
    throw new Error("Invalid date format. Expected format is YYYY-MM-DD.");
  }

  const edgeBrowsweDate = `${day}/${month}/${year}`;
  const chromeDate = `${year}/${month}/${day}`;

  if (isValid(new Date(edgeBrowsweDate))) {
    return edgeBrowsweDate;
  }
  return chromeDate;
}

export function base64ToFile(
  base64String: string,
  filename: string,
  mimeType = "application/octet-stream"
) {
  // Decode the Base64 string
  const byteString = atob(base64String.split(",")[1]);

  // Create an array of byte values
  const byteArray = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i++) {
    byteArray[i] = byteString.charCodeAt(i);
  }

  // Create a File object
  return new File([byteArray], filename, { type: mimeType });
}

export function getLastThreeCharacters(str: string): string {
  return str.replace(/\s+/g, "").slice(-3);
}


// Function to find the status type
export function findStatusType(str: string, statusTypes: StatusType[]) {
  // Remove extra spaces and get the last 3 characters of the input string
  const lastThree = str.replace(/\s+/g, "").slice(-3).toLowerCase();

  // Find the status type where a token in the name matches the last three characters
  return statusTypes.find((status: StatusType) => {
    // Split the sanitized name into words
    const tokens = status.name.replace(/\s+/g, " ").toLowerCase().split(" ");
    return tokens.includes(lastThree);
  });
}


export function capitalizeEachWord(str: string): string {
  if (!str) return str; // Return as is if the string is empty or falsy
  return str
    .split(' ') // Split the string into words
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize each word
    .join(' '); // Join the words back into a single string
}

export const validateInputDate = (inputValue:string,disableFutureDates = false,disablePastDates = false,isRequired = true) => {
  if(!isRequired) return
  // Regular expressions to match the supported date formats
  const formatsRegex = [
    /^\d{1,2}-\d{1,2}-\d{4}$/, // D-M-YYYY or DD-MM-YYYY
    /^\d{4}-\d{1,2}-\d{1,2}$/, // YYYY-M-D or YYYY-MM-DD
    /^\d{1,2}\/\d{1,2}\/\d{4}$/, // D/M/YYYY or DD/MM/YYYY
    /^\d{4}\/\d{1,2}\/\d{1,2}$/, // YYYY/M/D or YYYY/MM/DD
    /^\d{1,2}\/\d{1,2}\/\d{4}$/, // M/D/YYYY or MM/DD/YYYY
  ];

  // Check if the input matches any of the formats
  const isValidFormat = formatsRegex.some((format) =>
    format.test(inputValue),
  );
  if (!isValidFormat) {
    return "Invalid date format";
  }

  // Extract the year from the input
  const yearMatch = inputValue.match(/\d{4}/);
  if (yearMatch) {
    const year = parseInt(yearMatch[0], 10);

    // Check if the year is within a sensible range
    if (year < 1900 || year > 2100) {
  
      return "Year must be between 1900 and 2100";
    }
  }

  const formats = [
    "dd-MM-yyyy",
    "yyyy-MM-dd",
    "dd/MM/yyyy",
    "yyyy/MM/dd",
    "MM/dd/yyyy",
  ];

  // Attempt to parse the input value with the supported formats
  const parsedDate = formats
    .map((format) => parse(inputValue, format, new Date()))
    .find((date) => isValid(date));

  if (!parsedDate) {
    const errorMessage =
      "Invalid date format. Allowed formats are DD-MM-YYYY, YYYY-MM-DD, DD/MM/YYYY, MM/DD/YYYY, and YYYY/MM/DD";
   
    return errorMessage;
  }
  const today = new Date();
  const isFutureDate = disableFutureDates && isFuture(parsedDate);
  const isPastDate = disablePastDates && isBefore(parsedDate, today);

  if (isFutureDate) {
 
    return "Future date not allowed";
  }

  if (isPastDate) {

    return "Past date not allowed";
  }
}


export function parceDateStringToDate (dateString:string) {
     // Supported date formats
     const formats = [
      "dd-MM-yyyy",
      "yyyy-MM-dd",
      "dd/MM/yyyy",
      "yyyy/MM/dd",
      "MM/dd/yyyy",
    ];

    // Parse the date using date-fns
    const parsedDate = formats
      .map((format) => parse(dateString, format, new Date()))
      .find((date) => isValid(date));


    return parsedDate
}



  /**
   * Converts a string by removing "data.", dots, and hyphens, and converting to lowercase.
   * @param {string} str - The string to convert.
   * @returns {string} - The processed string in lowercase without "data.", dots, or hyphens.
   */
 export function toLowerNoSeparators(str: string) {
    return str
      .replace(/^data[.-]/, "")
      .replace(/[.-]/g, "")
      .toLowerCase();
  }

 
export const cleanValue = (value:string) => {
  return value.includes('-') 
    ? value.replace(/-/g, ' ').trim() 
    : value.trim();
};


/**
 * Normalizes a name by removing non-alphabetic characters and converting to lowercase.
 */
const normalizeName = (name: string): string =>
  name.replace(/[^a-zA-Z]/g, '').toLowerCase();

/**
 * Compares a user's full name (first + middle + last) to a given name (given + surname).
 * Returns true if they match after normalization.
 */
export function compareNames({
  profileFirstName,
  profileMiddleName,
  profileLastName,
  regulaGivenNames,
  regulaLastName,
}: NameComparisonParams): boolean {
  const fullName = normalizeName(`${profileFirstName}${profileMiddleName ?? ''}${profileLastName}`);
 
  const givenFullName = normalizeName(`${regulaGivenNames}${regulaLastName}`);
  return fullName === givenFullName;
}
/**
 * Compares a user's profile given names (first + middle ) to regula given names.
 * Returns true if they match after normalization.
 */
export function compareGivenNames({
  profileFirstName,
  profileMiddleName,

  regulaGivenNames,

}: GivenNamesComparisonParams): boolean {
  const fullName = normalizeName(`${profileFirstName}${profileMiddleName ?? ''}`);
 
  const givenFullName = normalizeName(`${regulaGivenNames}`);
  return fullName === givenFullName;
}
/**
 * Compares a user's profile last name (first + middle ) to regula last name.
 * Returns true if they match after normalization.
 */
export function compareLastNames({
  profileLastName,
  regulaLastName,
}: LastNameComparisonParams): boolean {
  const fullName = normalizeName(`${profileLastName}`);
 
  const givenFullName = normalizeName(`${regulaLastName}`);
  return fullName === givenFullName;
}



/**
 * Compares regula names (given names and/or last name) to profile names.
 * Returns true if at least one of the regula names matches any of the profile names.
 */
export function compareRegulaToProfileNames({
  profileFirstName,
  profileMiddleName,
  profileLastName,
  regulaGivenNames,
  regulaLastName,
}: NameComparisonParams): boolean {
  // Normalize all profile names
  const normalizedProfileNames = [
    normalizeName(profileFirstName),
    ...(profileMiddleName ? [normalizeName(profileMiddleName)] : []),
    normalizeName(profileLastName),
  ];

  // Normalize regula names
  const normalizedRegulaGivenNames = regulaGivenNames ? normalizeName(regulaGivenNames) : '';
  const normalizedRegulaLastName = regulaLastName ? normalizeName(regulaLastName) : '';

  // Check if any regula name matches any profile name
  const regulaNames = [normalizedRegulaGivenNames, normalizedRegulaLastName].filter(name => name.length > 0);
  
  return regulaNames.some(regulaName => 
    normalizedProfileNames.some(profileName => 
      regulaName === profileName || 
      profileName.includes(regulaName) || 
      regulaName.includes(profileName)
    )
  );
}


export const convertErrorsToString = (errors:ErrorObject) => {
  if (!errors || Object.keys(errors).length === 0) {
    return '';
  }

  return Object.entries(errors)
    .map(([field, errorObj]) => errorObj.en)
    .join('. ');
};

export const findRegulaFieldBySlug = (
  fields: RegulaDocumentField[],
  slug: string
): RegulaDocumentField | undefined => {
  return fields.find((field) => field.slug === slug);
};

interface AddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

interface GeocodingResult {
  address_components: AddressComponent[];
  formatted_address: string;
  geometry: {
    location: {
      lat: number;
      lng: number;
    };
    location_type: string;
    viewport: {
      south: number;
      west: number;
      north: number;
      east: number;
    };
  };
  place_id: string;
  types: string[];
}

interface ExtractedAddress {
  buildingNumber: string | null;
  buildingName: string | null;
  apartmentNumber: string | null;
  district: string | null;
  county: string | null;
  postalCode: string | null;
  street: string | null;
  town: string | null;
}


export function extractAddressFields(geocodingResult: GeocodingResult): ExtractedAddress {
  const components = geocodingResult.address_components || [];
  

  const findByType = (types: string[]): string | null => {
    const component = components.find(comp => 
      types.some(type => comp.types.includes(type))
    );
    return component ? component.long_name : null;
  };

  return {
    buildingNumber: findByType(['street_number']),
    buildingName: null, 
    apartmentNumber: findByType(['subpremise']),
    district: findByType(['sublocality', 'sublocality_level_1', 'administrative_area_level_3']),
    county: findByType(['administrative_area_level_2']),
    postalCode: findByType(['postal_code']),
    street: findByType(['route']),
    town: findByType(['locality', 'postal_town'])
  };
}